import type { Condition, FormLayout } from '@servicetim/shared';
import { matchOp } from '../fields/match';

export interface FormVisibility {
  /** Keys placed on the form whose `visibleWhen` is absent or evaluates true. */
  visible: Set<string>;
  /** Keys placed on the form that are hidden for the given values. */
  hidden: Set<string>;
}

/**
 * Evaluates every `visibleWhen` of a form layout against the submitted values (system fields
 * by name, custom fields by `cf_` key). A condition that points at a field which is itself
 * hidden hides its dependant too, so a chain of conditions never leaks a field through an
 * invisible parent. Fields are evaluated in section order; passes repeat until the hidden
 * set is stable (ADR-0013 §4: hidden values are stripped, `required` does not apply).
 */
export function resolveFormVisibility(
  layout: FormLayout,
  values: Record<string, unknown>,
): FormVisibility {
  const fields = layout.sections.flatMap((section) => section.fields);
  const conditions = new Map<string, Condition>();
  for (const field of fields) {
    if (field.visibleWhen) {
      conditions.set(field.key, field.visibleWhen);
    }
  }

  const hidden = new Set<string>();
  const pass = (): boolean => {
    let changed = false;
    for (const field of fields) {
      const condition = conditions.get(field.key);
      if (!condition || hidden.has(field.key)) {
        continue;
      }
      const parentHidden = hidden.has(condition.field);
      const matches = matchOp(condition.op, values[condition.field], condition.value);
      if (parentHidden || !matches) {
        hidden.add(field.key);
        changed = true;
      }
    }
    return changed;
  };
  // The hidden set only grows, so at most one pass per conditional field is ever needed.
  for (let i = 0; i <= conditions.size; i += 1) {
    if (!pass()) {
      break;
    }
  }

  const visible = new Set<string>();
  for (const field of fields) {
    if (!hidden.has(field.key)) {
      visible.add(field.key);
    }
  }
  return { visible, hidden };
}
